// run_feed — the run thread, grouped so it can be read.
//
// ── THE PROBLEM THIS SOLVES ──────────────────────────────────────────────────
//
// useChat appends ONE thread item per agent turn, plus ONE reasoning item before
// each of them. The `rounds` build (2026-09-22) ran more than 150 iterations across
// its two lanes, so its thread holds on the order of 300 items. The operator's words:
// "the feed on the platform ui is flooded with the ui talking and thinking".
//
// The fix is NOT more collapsing. `ReasoningBlock` is already a <details> collapsed
// by default, and 300 collapsed items are 300 fold bars — the same wall with smaller
// bricks. The fix is GROUPING: between two milestones, every turn folds into ONE row
// whose summary says how many steps, which files, and how long.
//
// This matches the industry's "Progress Steps" pattern, whose named anti-patterns we
// deliberately avoid (design_appgen_ui_study.md §2.1):
//   • expanding the trace by default and burying the answer  → chatter is folded,
//     milestones never are;
//   • omitting durations, making performance invisible       → `ms` is in the summary;
//   • vague labels like "Processing"                         → the summary names the
//     step count and the files, never "show more".
//
// ── WHY THIS MODULE IS PURE ──────────────────────────────────────────────────
//
// There is no component-test harness in this repo: platform-ui's tests are all pure
// modules (parse_actions, run_start, sanitize_narration, …). So the rule that matters —
// a milestone is NEVER folded — is only protectable if it lives in a function a test
// can call. It does, here. The renderer's remaining job is to switch exhaustively on
// FeedRow, which the compiler checks.
//
// ── ONE PARSER, NOT TWO ──────────────────────────────────────────────────────
//
// The summary reads `extractActions`/`extractAskQuestion` — the SAME functions the
// bubble renders from. A second parser over the same text would drift from the first,
// and the drift would show up as a summary that disagrees with the chips beside it.
import { extractActions, extractAskQuestion, type Action } from "./parse_actions";
import type { Message } from "../api";

/** What a folded group of turns says about itself, without being opened. */
export type StepsSummary = {
  /** Agent turns folded here. Counts only turns that RENDER something (see stepsOf). */
  steps: number;
  /** Files the agent WROTE, first-seen order, deduped, bounded by FILES_SHOWN. */
  files: string[];
  /** How many written files did not fit in `files`. 0 when they all did. */
  overflow: number;
  /**
   * How long this group took, in ms — or `null` when the thread cannot say.
   *
   * NEVER 0 as a stand-in. useChat stamps a missing `ts_ms` with `Date.now()` AT READ
   * TIME (api.ts/useChat), so on a thread replayed at page load every item can carry
   * the same instant; a duration computed from those would be 0, and 0 printed as a
   * duration is a false fact, not a missing one. The renderer shows nothing for null.
   */
  ms: number | null;
};

/**
 * The lane a row belongs to — DERIVED from Message so the two can never drift.
 *
 * Not re-declared as `string`: the badge component accepts only the known lanes, and a
 * widened type here would push a cast into the JSX. The narrow type also means a new
 * lane added to Message is a compile error at the badge, not a silent blank.
 */
export type Lane = NonNullable<Message["lane"]>;

export type FeedRow =
  /** Folded agent chatter: prose, tool chips and thinking, for one lane. */
  | { kind: "steps"; lane: Lane | null; items: Message[]; summary: StepsSummary; open: boolean }
  /** Never folded. See isMilestone. */
  | { kind: "milestone"; item: Message };

/** At most this many file names in a summary; the rest become `overflow`. */
export const FILES_SHOWN = 3;

/**
 * The tools that TOUCH a file. `view` and `list_files` are reads and must not appear
 * in "wrote …": a summary that claims the agent wrote a file it only opened is worse
 * than a summary with no files at all, because it reads as progress that never happened.
 */
//
// `edit` is here as well as the SEARCH/REPLACE EditAction: they are two different
// mechanisms that both write. The run log of 2026-09-22 shows the agent calling the
// `edit` TOOL with a `path` (`tool-call edit {"path":"api/rounds_plan.ts","search":…}`),
// while parse_actions' EditAction comes from a SEARCH/REPLACE fence. Covering only one
// of them would under-report writes on whichever style the agent happened to use.
const WRITING_TOOLS = new Set(["create", "delete", "edit"]);

/**
 * Les outils dont l'appel touche à ce qui appartient à l'OPÉRATEUR, et pas à l'app.
 *
 * `use_asset` fait du fichier qu'il vient de coller — son logo, la photo de son équipe — un
 * fichier de son app, servi en clair à `/assets/<nom>` et porté dans chaque construction. C'est
 * SA donnée, elle devient publique, et c'est permanent jusqu'à ce qu'il demande autre chose.
 *
 * Un tour d'outils replié est le bon défaut pour les trois cents tours d'une construction. Pas
 * pour celui-là : l'opérateur doit pouvoir voir que son fichier est arrivé, et sous quel nom.
 */
const OPERATOR_OWNED_TOOLS = new Set(["use_asset"]);

/**
 * A milestone is a row the operator MUST see, so it is never folded.
 *
 * The three that exist in the thread today:
 *
 *   1. the PLATFORM's verdict (`kind: "verification"`) — the one line in the thread the
 *      agent did not write, and the only checkable claim about whether the app works;
 *   2. the OPERATOR's own message — folding someone's own words is absurd;
 *   3. an assistant turn carrying an `ask` — its question is what the run is WAITING on.
 *      Folding it would leave the run blocked with nothing visible to answer. The run 1
 *      of `rounds` sat 27 s on such a question; had it been folded, it would have sat
 *      there until the operator gave up.
 *
 * The question is recognised the way the renderer recognises it — through the parsed
 * actions, never by looking for words in the text. A milestone decided by string
 * matching would be missed the day the wording changes, and a missed milestone HIDES
 * the reason a run is not moving.
 *
 * ADDED 2026-09-22: `plan`, `part`, and `failure` — the last after a live incident where a build
 * died and the feed said nothing. See the branches above.
 *
 * STILL NOT HERE: a guardrail REFUSAL and an individual tool failure (a run-ending failure IS here,
 * see `failure` above). They are lines that explain why a build is not progressing, and they belong
 * in this list — but they do not reach the client at all today. useChat maps exactly four inner events
 * (reasoning, e2e-corroboration, assistant-message, question); tool results never
 * become thread items, and the only failure signal anywhere in the client is the word
 * "(failed)" inside a chip's own display label (parse_actions.actionLabel). Adding them
 * means emitting typed events server-side. Until that exists, this function must not
 * pretend: inferring a refusal from a label would be exactly the string-matching this
 * comment forbids one paragraph above.
 */
export function isMilestone(m: Message): boolean {
  if (m.kind === "verification") return true;
  // A CHAT REPLY, never folded. The grouping exists for one measured problem — a build of
  // 300 tool turns — and a conversation does not have it: the operator asks, the platform
  // answers, and that answer IS the product of the exchange. Measured before this line
  // existed: [question, reply] grouped as `milestone, steps(1)`, so the question stood in
  // the clear and its answer sat folded behind a bar.
  //
  // `mode` is persisted by the thread (lib/thread_store), so this holds after a reload —
  // which is why the rule is written on `mode` and not on a client-only marker.
  //
  // Empty text is excluded for the same reason `rendersSomething` excludes it: a row the
  // operator cannot find however hard they look is worse than no row.
  if (m.role === "assistant" && m.mode !== "build" && m.kind === undefined) return m.text.trim().length > 0;
  // The plan and its part boundaries are the feed's SPINE: the plan is what the operator approved,
  // and a part header is the only place "3 / 10" is written. Folding either would remove the one
  // thing that makes a ten-part build readable — and a plan folded inside a steps row would be an
  // approval the operator never saw.
  if (m.kind === "plan" || m.kind === "part") return true;
  // LA REVUE DES PARCOURS, pour la raison exacte que `plan` donne juste au-dessus : c'est ce que
  // l'opérateur APPROUVE, et le run est à l'arrêt tant qu'il n'a pas répondu.
  //
  // Elle manquait, et sa plainte du 2026-10-06 le disait : « the summary of what your app will do
  // still shows up in collapsible ». Le serveur l'envoie en trois événements (run_loop, bloc
  // journeyReview) : le typé, puis sa PROSE en `assistant-message`, puis la `question`. Cette
  // prose n'a ni `ask` dans son texte ni `kind` — donc toutes les règles ci-dessus la laissaient
  // tomber dans le bavardage replié. L'opérateur voyait la question en clair et le plan qu'elle
  // concerne caché derrière une barre.
  //
  // Le `kind` est posé par useChat quand l'événement TYPÉ `journey-review` précède la prose —
  // jamais en cherchant des mots dans le texte, la règle de ce fichier.
  if (m.kind === "review") return true;
  // A FAILURE is the line that explains why nothing is moving. Folded, the operator is back to the
  // 2026-09-22 situation: a build that stopped and a thread that says nothing about it.
  if (m.kind === "failure") return true;
  if (m.role === "user") return true;
  if (m.role === "assistant" && m.mode === "build" && m.kind === undefined) {
    const { actions } = extractActions(m.text);
    if (extractAskQuestion(actions) !== null) return true;
    // UN FICHIER DE L'OPÉRATEUR DEVIENT UN FICHIER PUBLIC DE SON APP. Reconnu sur le NOM DE
    // L'ACTION, comme `writtenPathsOf` juste en dessous — jamais sur des mots dans la prose,
    // qui est la règle de ce fichier : « a milestone decided by string matching would be missed
    // the day the wording changes ».
    if (actions.some((a) => a.kind !== "edit" && OPERATOR_OWNED_TOOLS.has(a.name))) return true;
  }
  return false;
}

/**
 * Does this turn put anything on screen?
 *
 * The bubble renders nothing at all for a build turn with neither prose nor actions
 * (Workspace: `if (!showProse && !showActions) return null`). Counting such a turn as
 * a step would inflate "14 steps" with turns the operator cannot find however hard they
 * look for them — a number that cannot be checked against the screen.
 *
 * A reasoning item is folded WITH its turn but is not itself a step: it is the thinking
 * for the answer that follows, not a second action.
 */
function rendersSomething(m: Message): boolean {
  if (m.kind === "reasoning") return false;
  // NOTE, after a mutation run: there is deliberately NO branch here for `plan` and `part`. They are
  // milestones, so they never reach a steps bucket, and a guard that cannot fire is the "second
  // mechanism for one invariant" this codebase treats as the next divergence — it would also give a
  // false sense that demoting a milestone is safe. The single mechanism is isMilestone.
  if (m.role !== "assistant") return false;
  if (m.mode !== "build") return m.text.trim().length > 0;
  const { prose, actions } = extractActions(m.text);
  return prose.trim().length > 0 || actions.length > 0;
}

/** The files an action WROTE — never the ones it read. */
function writtenPathsOf(actions: readonly Action[]): string[] {
  const out: string[] = [];
  for (const a of actions) {
    if (a.kind === "edit") { if (a.path) out.push(a.path); continue; }
    if (!WRITING_TOOLS.has(a.name)) continue;
    const p = a.input?.path;
    if (typeof p === "string" && p.trim()) out.push(p.trim());
  }
  return out;
}

/** ms since epoch for a thread item, or null when the stamp is unusable. */
function stampOf(m: Message): number | null {
  const t = Date.parse(m.ts);
  return Number.isFinite(t) ? t : null;
}

function summarize(items: readonly Message[]): StepsSummary {
  let steps = 0;
  const seen = new Set<string>();
  const files: string[] = [];
  let written = 0;
  for (const m of items) {
    if (rendersSomething(m)) steps++;
    if (m.role === "assistant" && m.mode === "build" && m.kind === undefined) {
      for (const p of writtenPathsOf(extractActions(m.text).actions)) {
        if (seen.has(p)) continue;
        seen.add(p);
        written++;
        if (files.length < FILES_SHOWN) files.push(p);
      }
    }
  }
  // A duration needs two DIFFERENT usable stamps. Equal stamps are the page-load
  // case described on StepsSummary.ms — unknowable, so null.
  const stamps = items.map(stampOf).filter((t): t is number => t !== null);
  const ms = stamps.length >= 2 ? Math.max(...stamps) - Math.min(...stamps) : null;
  return { steps, files, overflow: written - files.length, ms: ms === 0 ? null : ms };
}

/**
 * The lane a turn belongs to, or null when the thread does not say (a reasoning item,
 * a chat reply, a single-agent run, a turn older than the field).
 */
function laneOf(m: Message): Lane | null {
  return m.lane ?? null;
}

/**
 * Group a thread into rows the operator can read.
 *
 * `live` marks the LAST row open, so the operator watches the current work happen and
 * it folds when its milestone closes it. Without that, the feed would fold itself under
 * the operator's eyes on every new item — a fold that moves on its own is worse than a
 * long thread.
 *
 * ── GROUPS BREAK PER LANE ──
 *
 * The backend and frontend lanes run in PARALLEL and post into the SAME thread,
 * interleaved (`m.lane`, stamped by useChat and persisted by the server). One group
 * across both would summarise "wrote App.tsx, geocode.ts" — a turn of phrase in which
 * the operator can no longer tell who did what, and in which a frontend refusal reads
 * as a backend problem. So a lane change closes the group.
 *
 * A turn with NO lane never breaks a group: a reasoning item carries none, and it
 * arrives immediately before the laned turn it belongs to. Treating "no lane" as a
 * third lane would split every single turn from its own thinking.
 */
export function groupFeed(
  messages: readonly Message[],
  opts: {
    live?: boolean;
    /**
     * Is the run WAITING on the operator right now?
     *
     * When it is, the last assistant turn is never folded — it is what the operator is being asked
     * about. The rule is not new: an assistant turn carrying an `ask` fence is already a milestone,
     * for exactly this reason. What was incomplete was the TRIGGER: the platform's journey review
     * delivers its question as a separate event, so the brief it wrote — "here is what your app
     * will let people do" — read as ordinary prose and was folded into a steps row. The operator
     * was asked to approve something the interface had just hidden.
     */
    awaitingAnswer?: boolean;
  } = {},
): FeedRow[] {
  const rows: FeedRow[] = [];
  let bucket: Message[] = [];
  let bucketLane: Lane | null = null;

  const flush = () => {
    if (bucket.length === 0) return;
    rows.push({ kind: "steps", lane: bucketLane, items: bucket, summary: summarize(bucket), open: false });
    bucket = [];
    bucketLane = null;
  };

  // The last assistant turn WITH PROSE, when one is owed an answer.
  //
  // Prose and not merely text: a turn that is only a tool fence has plenty of text and nothing to
  // read. Opening one would give the operator a row of machinery to approve while the brief stayed
  // folded above it — the same failure, one line lower. `extractActions` is the same separation
  // the bubble itself renders with, so what is opened is what can be read.
  //
  // Found once, by identity, so the check inside the loop is a comparison and not a search.
  const awaited = opts.awaitingAnswer
    ? [...messages].reverse().find((m) => m.role === "assistant" && extractActions(m.text).prose.trim().length > 0) ?? null
    : null;

  // ── AND WHAT THE OPERATOR ALREADY ANSWERED (2026-10-01) ──
  //
  // `awaited` covers the question while it is open. It stopped covering it the instant the
  // operator replied: measured on the real thread
  // [tool turn, brief, "yes", tool turn] → `steps(2), milestone, steps(1)`, with the brief
  // inside that first fold. So the APPROVED plan — the one thing the operator signed off in
  // the whole run — vanished into a collapsible, leaving their "yes" on screen above nothing.
  //
  // The rule is the same one, made durable: what the operator is approving is never folded,
  // and neither is what they approved. It is decided by STRUCTURE — an assistant turn
  // immediately before a message of theirs — never by the words in the text, because the
  // persisted thread carries only {role, text, mode, ts, lane} and a client-side marker
  // would be lost on reload.
  //
  // WHAT THE WALK BACK SKIPS, and why it is the same list `awaited` uses: a reasoning item
  // (live, one arrives before the turn it belongs to) and a turn that is only a tool fence
  // ("plenty of text and nothing to read" — opening one would give the operator a row of
  // machinery while the brief stayed folded above it). It stops at the first turn that HAS
  // prose: the one they were answering.
  //
  // That stop is what keeps the rule narrow. Without it the walk would mark EVERY earlier
  // prose turn, and the feed would stop folding at all — the 300-line wall this module
  // exists to prevent.
  //
  // THERE IS DELIBERATELY NO `break` ON AN EARLIER OPERATOR MESSAGE. It reads like the
  // obvious guard ("they were answering nothing of ours") and a mutation run proved it
  // cannot change the result: anything reachable past an operator message was already
  // marked by THAT message's own walk, since both walks skip the same things in the same
  // direction and stop at the same first prose turn. A guard that cannot fire is the
  // second mechanism for one invariant this module refuses elsewhere (see the note in
  // rendersSomething), so it is not here.
  const answered = new Set<Message>();
  for (let i = 0; i < messages.length; i++) {
    if (messages[i]!.role !== "user") continue;
    for (let j = i - 1; j >= 0; j--) {
      const before = messages[j]!;
      if (before.kind === "reasoning") continue;
      if (before.role === "assistant" && extractActions(before.text).prose.trim().length > 0) { answered.add(before); break; }
    }
  }

  for (const m of messages) {
    if (m === awaited || answered.has(m) || isMilestone(m)) { flush(); rows.push({ kind: "milestone", item: m }); continue; }
    const lane = laneOf(m);
    if (lane !== null && bucketLane !== null && lane !== bucketLane) flush();
    bucket.push(m);
    if (lane !== null && bucketLane === null) bucketLane = lane;
  }
  flush();

  if (opts.live) {
    const last = rows[rows.length - 1];
    if (last && last.kind === "steps") last.open = true;
  }
  return rows;
}

// ── how a folded row describes itself ───────────────────────────────────────
//
// In this module, not in the JSX, because the summary line is the exact thing the
// Progress Steps anti-patterns are about: a vague label ("Processing") and a missing
// duration are both failures of THIS string. A label built inline in a component
// cannot be tested in this repo, so it would be the one part of the fold nothing
// protects.

/**
 * A duration for a person: "<1s", "12s", "3m12s", "1h04m". Null in → empty string out.
 *
 * Sub-second work reads "<1s", NOT "0s". Caught by its own test while writing this:
 * `Math.round(400 / 1000)` is 0, so the first version printed "0s" for 400 ms of real
 * work — the fake zero this module forbids three lines above, reintroduced by rounding.
 * "<1s" is both true and useful; "" would hide that the group ran at all.
 */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "";
  if (ms < 1000) return "<1s";
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/**
 * The one line a folded row shows while closed.
 *
 * Reads as facts, never as an invitation: how many steps, which files were written,
 * how long. It never says "show more" — the fold arrow already says that, and a label
 * that only says "more" is the vague label the pattern warns against.
 *
 * A part that has nothing to say is OMITTED rather than padded: no files → no "0 files",
 * no duration → no "0s". Everything in this string is something that happened.
 */
export function summaryLabel(s: StepsSummary): string {
  const parts: string[] = [`${s.steps} step${s.steps === 1 ? "" : "s"}`];
  if (s.files.length > 0) {
    parts.push(`wrote ${s.files.join(", ")}${s.overflow > 0 ? ` +${s.overflow}` : ""}`);
  }
  const d = formatDuration(s.ms);
  if (d) parts.push(d);
  return parts.join(" · ");
}
